import { Injectable } from '@angular/core';
import {
    BehaviorSubject,
    catchError,
    Observable,
    of,
    shareReplay,
    tap,
} from 'rxjs';
import {
    ICreateAuthenticationCodeResult,
    IContractSigningResponse,
    IInetuserLogSize,
    IPatient,
    IResponseGetRecordLog,
    ITokenAndPatientId,
    IUnsignedPayContract,
} from '../interfaces/patient.interfaces';
import { HttpNewService } from './application/http-new.service';
import { AuthService } from './auth.service';
import { EventService } from './event/event.service';
@Injectable({
    providedIn: 'root',
})
export class PatientService {
    public firstName = '';
    public lastName = '';
    public secondName = '';
    public birthDate = '';

    private patientSubject = new BehaviorSubject<IPatient | null>(null);
    public patient$ = this.patientSubject.asObservable();

    public get currentPatient(): IPatient | null {
        return this.patientSubject.value;
    }

    public constructor(
        private httpNew: HttpNewService,
        private auth: AuthService,
        private eventService: EventService,
    ) {
        // вот это под вопросом - надо ли фетчить это постоянно
        this.fetchPatientInfo();
        eventService.on('auth:logout', this.clearPatientInfo);
        // второе под вопросом, при авторизации оно и так уже дергается, соответственно а зачем второй раз? с токеном? так на беке он уже есть. посоветоваться.
        eventService.on('auth:login-success', (data: ITokenAndPatientId) => {
            this.refreshPatientInfo(data.token);
        });
    }

    private refreshPatientInfo(token: string): void {
        this.getServerPatientInfo$(token)
            .pipe(
                catchError((err) => {
                    console.error(
                        'Ошибка получения данных нового пациента:',
                        err,
                    );
                    this.patientSubject.next(null);
                    return of(null);
                }),
            )
            .subscribe({
                next: (res) => {
                    if (res && 'lastName' in res && 'firstName' in res) {
                        this.patientSubject.next(res);
                        // Update individual properties
                        this.firstName = res.firstName;
                        this.lastName = res.lastName;
                        this.secondName = res.secondName;
                        this.birthDate = res.birthDateStr;
                    }
                },
            });
    }
    public updatePatientInfo(patient: IPatient): void {
        this.patientSubject.next(patient);

        this.firstName = patient.firstName;
        this.lastName = patient.lastName;
        this.secondName = patient.secondName;
        this.birthDate = patient.birthDateStr;
    }
    public fetchPatientInfo(): Observable<IPatient | null> {
        return this.getServerPatientInfo$().pipe(
            tap((res) => {
                if (res && 'lastName' in res && 'firstName' in res) {
                    this.patientSubject.next(res); // Сохраняем в сабджект
                } else {
                    console.warn(
                        'Данные пациента не соответствуют ожидаемой структуре',
                        res,
                    );
                    this.patientSubject.next(null); // Сбрасываем, если структура битая
                }
            }),
            catchError((err) => {
                console.error(
                    'Ошибка получения данных пациента:',
                    err.status,
                    err.message,
                );
                this.patientSubject.next(null); // Сбрасываем при ошибке
                try {
                    this.auth.logout();
                } catch (e) {
                    console.error('Ошибка при попытке выхода:', e);
                }
                return of(null);
            }),
            shareReplay({ bufferSize: 1, refCount: true }),
        );
    }
    // public fetchPatientInfo(): void {
    //     this.getServerPatientInfo$()
    //         .pipe(
    //             tap((res) => {
    //                 if (res && 'lastName' in res && 'firstName' in res) {
    //                     this.patientSubject.next(res);
    //                 } else {
    //                     console.warn(
    //                         'Данные пациента не соответствуют структуре',
    //                         res,
    //                     );
    //                     this.patientSubject.next(null);
    //                 }
    //             }),
    //             catchError((err) => {
    //                 console.error(
    //                     'Ошибка получения данных пациента:',
    //                     err.status,
    //                     err.message,
    //                 );
    //                 this.patientSubject.next(null);
    //                 try {
    //                     this.auth.logout();
    //                 } catch (e) {}
    //                 return of(null);
    //             }),
    //         )
    //         .subscribe();
    // }

    public getServerPatientInfo$(
        token?: string,
        isFamilyAccount?: boolean,
    ): Observable<IPatient> {
        return this.httpNew.get<IPatient>(`/user/info`, {
            token: token || null,
            isFamilyAccount: isFamilyAccount || false,
        });
    }

    public clearPatientInfo(): void {
        this.lastName = '';
        this.secondName = '';
        this.firstName = '';
        this.birthDate = '';
        //this.patientSubject.next(null);
    }

    public get getFullName(): string {
        return [this.lastName, this.firstName, this.secondName].join(' ');
    }

    public get getBirthdate(): string {
        return this.birthDate;
    }

    /* Смена пароля*/
    public changepasswd$(
        pOldPassword: string,
        pNewPassword: string,
    ): Observable<any> {
        return this.httpNew.post<void>('/user/change_password', {
            oldPassword: pOldPassword,
            newPassword: pNewPassword,
        });
    }

    /* Количество активности пользователя*/
    public getInetuserLogSize$(): Observable<IInetuserLogSize> {
        return this.httpNew.get<IInetuserLogSize>('/log/size');
    }

    /* Список активности пользователя*/
    public getInetuserLog$(
        pStart?: number,
        pEnd?: number,
    ): Observable<Array<IResponseGetRecordLog>> {
        let url = '/log/rec';
        if (pStart && pEnd) {
            url = url + `?start=${pStart}&end=${pEnd}`;
        }
        return this.httpNew.get<Array<IResponseGetRecordLog>>(url);
    }

    // Запись параметров, формирование заявления и его отправка
    public getStatementSave$(pTypeId: number, pDate: string): Observable<void> {
        return this.httpNew.post<void>(`/statement/create`, {
            statementParams: pDate,
        });
    }

    public getPayContract(): Observable<Array<IUnsignedPayContract>> {
        //TomCat      const url = '/api/patient/' + this.patientId + '/contract/sign';
        const url = '/contract/paycontract';
        return this.httpNew.get<Array<IUnsignedPayContract>>(
            '/contract/paycontract',
            null,
        );
    }

    private patientNameSubject = new BehaviorSubject<string>('');
    public patientName$ = this.patientNameSubject.asObservable();

    public setPatientName(patientName: string) {
        this.patientNameSubject.next(patientName);
    }
}
